/**
 * The URL chooses the tool and the default format (IMAGE_EDITOR_V2.md R4/R5): `crop-jpg` opens on
 * Crop saving as JPG; `/image-resizer` opens on Resize saving as the original format. One table,
 * shared by all 69 per-format slugs plus the generic ones. Pure; tested in routing.spec.ts.
 */
import type { Tool } from './model/edit-stack';
import type { ImageFormat } from './codecs/types';

export interface LandingSpec {
    slug: string;
    tool: Tool;
    /** 'original' = "Same as original" (R5); otherwise the page's format. */
    defaultFormat: ImageFormat | 'original';
    /** The format the page is about, for copy ("Crop a JPG"), or null for generic pages. */
    pageFormat: ImageFormat | null;
    /** Human name used in the empty state and the button. */
    formatLabel: string | null;
}

const TOOL_BY_PREFIX: Record<string, Tool> = { resize: 'resize', crop: 'crop', rotate: 'rotate', flip: 'flip' };

/** Extension token in a slug → editor format. Tokens the editor cannot save to still map to a format
 *  (heic, apng) so the copy can name them; the codec fallback decides what is actually written. */
const FORMAT_BY_TOKEN: Record<string, ImageFormat> = {
    jpg: 'jpg', jpeg: 'jpg', jfif: 'jpg', png: 'png', apng: 'apng', webp: 'webp', gif: 'gif', bmp: 'bmp',
    tiff: 'tiff', tif: 'tiff', ico: 'ico', avif: 'avif', heic: 'heic', heif: 'heic', psd: 'psd', ppm: 'ppm', eps: 'eps',
};

const LABEL_BY_FORMAT: Record<ImageFormat, string> = {
    jpg: 'JPG', png: 'PNG', webp: 'WebP', gif: 'GIF', apng: 'APNG', bmp: 'BMP', tiff: 'TIFF', ico: 'ICO', avif: 'AVIF',
    heic: 'HEIC', psd: 'PSD', ppm: 'PPM', eps: 'EPS', raw: 'RAW', unknown: 'image',
};

export function landingFor(slugOrPath: string | undefined | null): LandingSpec {
    const slug = (slugOrPath || '').replace(/^\/+/, '').split(/[?#]/)[0].split('/')[0].toLowerCase();
    if (slug === 'image-resizer' || slug === '') return { slug: slug || 'image-resizer', tool: 'resize', defaultFormat: 'original', pageFormat: null, formatLabel: null };
    const m = /^(resize|crop|rotate|flip)-([a-z0-9]+)$/.exec(slug);
    if (!m) return { slug, tool: 'resize', defaultFormat: 'original', pageFormat: null, formatLabel: null };
    const tool = TOOL_BY_PREFIX[m[1]];
    const token = m[2];
    if (token === 'image' || token === 'images' || token === 'photo' || token === 'photos') {
        return { slug, tool, defaultFormat: 'original', pageFormat: null, formatLabel: null };
    }
    const format = FORMAT_BY_TOKEN[token];
    if (!format) return { slug, tool, defaultFormat: 'original', pageFormat: null, formatLabel: null };
    return { slug, tool, defaultFormat: format, pageFormat: format, formatLabel: LABEL_BY_FORMAT[format] };
}

export function formatLabel(format: ImageFormat): string { return LABEL_BY_FORMAT[format] ?? format.toUpperCase(); }

/** Copy for the empty state, derived from the landing. Titles/H1 still come from page data. */
export function emptyStateCopy(l: LandingSpec): { verb: string; noun: string; button: string } {
    const verbs: Record<Tool, string> = { resize: 'Resize', crop: 'Crop', rotate: 'Rotate', flip: 'Flip' };
    const noun = l.formatLabel ? `a ${l.formatLabel}` : 'an image';
    return { verb: verbs[l.tool], noun, button: l.formatLabel ? `Choose ${l.formatLabel} files` : 'Choose photos' };
}
